import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Search,
  Database,
  ClipboardList,
  RefreshCw,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: Search,
    title: "OT Asset Discovery & Inventory",
    text: "Erfassung und Klassifizierung aller OT-Komponenten in Ihrer Gebäudeautomation: BACnet-Controller, KNX-Teilnehmer, Feldgeräte, Netzwerkkomponenten, Fernzugriffe und Cloud-Dienste, inklusive Standort, Verantwortlichkeit und Lebenszyklus.",
  },
  {
    Icon: Database,
    title: "CMDB & Asset-Register",
    text: "Aufbau eines wartbaren Asset-Registers oder CMDB für OT, das nicht nur Listen liefert, sondern Grundlage für Risikobewertung, Incident Response, Patch-Management und Prüfungen nach IEC 62443 sowie NIS-2 wird.",
  },
  {
    Icon: ClipboardList,
    title: "Risikoklassifizierung & Security Level",
    text: "Bewertung Ihrer OT-Assets nach Kritikalität, Exposition und Schutzbedarf. Ableitung von Target Security Levels nach IEC 62443 und Priorisierung von Maßnahmen für Zonen und Conduits.",
  },
  {
    Icon: RefreshCw,
    title: "Lifecycle-, Patch- & Change-Prozesse",
    text: "Einführung tragfähiger Prozesse für Wartung, Updates, Austausch und Änderungen an OT-Assets, verzahnt mit IT, Facility und Integratoren, damit das Asset-Register lebendig bleibt.",
  },
];

const benefits = [
  "Sie wissen, welche OT-Assets in Ihren Liegenschaften laufen, wer dafür zuständig ist und wie lange sie noch supportet werden.",
  "Sie schaffen die dokumentarische Basis, die NIS-2, IEC 62443 und BSI INF.13 / INF.14 verlangen.",
  "Sie priorisieren Investitionen nach Risiko statt nach Bauchgefühl.",
  "Sie verkürzen Reaktionszeiten bei Incidents, weil Sie sofort wissen, was betroffen ist.",
  "Sie etablieren eine gemeinsame Sprache zwischen Betreiber, IT, OT, Facility und Lieferanten.",
];

const faqs = [
  {
    q: "Was ist OT Asset Management?",
    a: "OT Asset Management ist die systematische Erfassung, Klassifizierung und Pflege aller technischen Komponenten in Operational Technology, also in industriellen Steuerungs- und Gebäudeautomationssystemen. Es umfasst Hardware, Software, Firmware, Netzwerk, Fernzugriffe und Verantwortlichkeiten.",
  },
  {
    q: "Warum reicht klassisches IT Asset Management nicht aus?",
    a: "IT-Asset-Management-Tools sind oft nicht auf die Besonderheiten von OT ausgelegt: lange Lebenszyklen, proprietäre Protokolle, eingebettete Systeme, Safety-Aspekte und den engen Verzug mit Facility-Betrieb. Ein OT-Asset-Register berücksichtigt diese Unterschiede und ist auf Gebäudeautomation zugeschnitten.",
  },
  {
    q: "Wie hängt OT Asset Management mit IEC 62443 und NIS-2 zusammen?",
    a: "Beide Standards setzen voraus, dass Organisationen ihre Systeme kennen, bevor sie Sicherheitsmaßnahmen planen. IEC 62443 verlangt Assetmanagement als Grundlage für Zonen- und Conduit-Konzepte und Security Levels. NIS-2 verlangt Risikomanagement und Dokumentation der Netz- und Informationssysteme, zu denen auch Gebäudeautomation gehört.",
  },
  {
    q: "Was erfasse ich als OT-Asset?",
    a: "Neben Geräten wie DDC-Controllern, BACnet- und KNX-Komponenten, Feldgeräten, Switches, Routern und Fernzugriffslösungen erfassen wir auch Firmware-Versionen, Zertifikate, Wartungsverträge, Verantwortliche, Standorte, Schnittstellen zu IT und Cloud sowie kritische Abhängigkeiten.",
  },
  {
    q: "Welche Tools kommen zum Einsatz?",
    a: "Je nach Ausgangslage arbeiten wir mit einfachen strukturierten Tabellen, CMDB-Lösungen oder spezialisierten OT-Discovery-Tools. Wichtiger als das Tool ist das Prozessmodell: Wer pflegt Daten, wie oft, und wie fließen Änderungen zurück in das Register.",
  },
  {
    q: "Wie starten wir konkret?",
    a: "Der pragmatische Einstieg ist ein OT Asset Discovery Sprint für eine repräsentative Liegenschaft oder Anlage. Alternativ starten Sie mit dem FACHSPRECHPARTNER Workbook inklusive Self-Assessment und 60 Minuten Online Assessment, um aus der Bestandsaufnahme einen priorisierten Fahrplan abzuleiten.",
  },
];

const OtAssetManagement = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/ot-asset-management";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "OT Asset Management für Gebäudeautomation",
        serviceType: "OT Asset Management und Asset Inventory",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "OT Asset Management für Gebäudeautomation: Discovery, CMDB, Risikoklassifizierung und Lifecycle-Prozesse nach IEC 62443 und NIS-2.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "OT Asset Management", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>OT Asset Management für Gebäudeautomation & OT</title>
        <meta
          name="description"
          content="OT Asset Management für Gebäudeautomation: Discovery, CMDB, Risikoklassifizierung und Lifecycle-Prozesse nach IEC 62443 und NIS-2. Assessment und Workbook."
        />
        <meta
          name="keywords"
          content="OT Asset Management, OT Asset Inventory, Gebäudeautomation Asset Management, OT CMDB, IEC 62443 Asset Management, NIS-2 Asset Management, OT Dokumentation, BACnet Asset Discovery"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="OT Asset Management für Gebäudeautomation & OT" />
        <meta
          property="og:description"
          content="OT Asset Management für Gebäudeautomation: Discovery, CMDB, Risikoklassifizierung und Lifecycle-Prozesse nach IEC 62443 und NIS-2."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="OT Asset Management für Gebäudeautomation & OT" />
        <meta name="twitter:description" content="OT Asset Management für Gebäudeautomation: Discovery, CMDB, Risikoklassifizierung und Lifecycle-Prozesse nach IEC 62443 und NIS-2." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck className="w-4 h-4" />
                OT Asset Management · Gebäudeautomation
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                OT Asset Management: Ordnung schaffen, bevor Sicherheit gefordert ist.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich helfe Ihnen, Ihre OT-Assets in Gebäudeautomation und industriellen
                Umgebungen zu erfassen, zu klassifizieren und lebendig zu halten. Damit
                schaffen Sie die Basis für IEC 62443, NIS-2 und einen sicheren Betrieb.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen rund um OT Asset Management
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, die aus einer unübersichtlichen OT-Landschaft ein
              wartbares, prüfbares und sicherheitsrelevantes Asset-Register machen.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Was Sie davon haben
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                OT Asset Management ist der erste Schritt zu einer beherrschbaren
                Sicherheitsarchitektur. Wer seine Assets nicht kennt, kann sie weder
                schützen noch wirtschaftlich betreiben.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zu OT Asset Management
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ihre OT-Assets im Blick behalten
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie mit einem Discovery Sprint oder dem Workbook. In beiden
              Fällen bekommen Sie ein strukturiertes Asset-Register und einen
              priorisierten Fahrplan für sichere Gebäudeautomation.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default OtAssetManagement;
